/**
 * Goal 55B — how a screen words a FAILED analysis job's `error`.
 *
 * Most errors are fragments («manca il listino», a raw exception) and need the screen's frame in
 * front of them: «Analisi non riuscita: manca il listino». Two of the server's sentences do not:
 *
 *   - «Analisi non salvata: …» (`runner._not_saved_message`, `user_work.check_before_replace`):
 *     items left unread, or the user's work changed during the run — it names what was not
 *     saved, says the previous result is unchanged and what to do;
 *   - «Analisi fermata …» (the Step 1 limits, `cmeinsight…budget`): why the run stopped, items
 *     read, spend against its limit, and that the saved result was not modified;
 *   - «Analisi fermata prima della prima chiamata a pagamento: …» (the bill pre-flight and the
 *     duplicate-role backstop, `bill_preflight.STOPPED_OPENING`): the file and why it stopped a run
 *     whose extraction never started — tender-api/tests/test_g55b_i_preflight_opening.py checks
 *     that this marker stays one of the openings below.
 *
 * Framed, each would say the same thing twice with two different verbs («Analisi non riuscita:
 * Analisi non salvata: …»), so every surface shows them AS THEY ARE: the completion toast
 * (`JobsWatcher`), the preparation screen's notice and outcome card (`UploadView`) and the Audit
 * table (`UnifiedTable`). One predicate, so the four cannot drift apart.
 */
const SELF_CONTAINED_OPENINGS = ["Analisi non salvata:", "Analisi fermata"] as const;

/** True when the server's sentence already stands on its own and must not be framed. */
export function isSelfContainedFailure(error: string | null | undefined): error is string {
  return !!error && SELF_CONTAINED_OPENINGS.some((opening) => error.startsWith(opening));
}

/**
 * The line a screen shows for a failed job: the server's sentence as it is when it stands on its
 * own; otherwise `${frame}: ${error}`, or `${frame}${whenEmpty}` when there is no error at all.
 */
export function failureLine(
  frame: string,
  error: string | null | undefined,
  whenEmpty: string = ".",
): string {
  if (isSelfContainedFailure(error)) return error;
  return error ? `${frame}: ${error}` : `${frame}${whenEmpty}`;
}
